<template>
  <div class="container">
    <el-card shadow="never">
      <el-form :inline="true" :model="filter">
        <el-form-item label="类型">
          <el-select v-model="filter.type" clearable placeholder="全部" style="width: 200px;">
            <el-option label="满意度问卷结果邮件" value="stat.questionnaire" />
            <el-option label="企微ID数量统计邮件" value="stat.wework" />
          </el-select>
        </el-form-item>
        <el-form-item label="状态">
          <el-select v-model="filter.status" clearable placeholder="全部" style="width: 160px;">
            <el-option v-for="(label, key) in statusMap" :key="key" :label="label" :value="key" />
          </el-select>
        </el-form-item>
        <el-form-item label="收件人">
          <el-input v-model="filter.to" clearable placeholder="邮箱关键字" />
        </el-form-item>
        <el-form-item label="日期">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            value-format="yyyy-MM-dd"
            start-placeholder="开始"
            end-placeholder="结束"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="search">查询</el-button>
        </el-form-item>
      </el-form>

      <el-table :data="list" v-loading="loading" border>
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="type" label="类型" width="180" />
        <el-table-column prop="biz_key" label="业务键" min-width="220" />
        <el-table-column label="状态" width="110">
          <template slot-scope="scope">
            <el-tag :type="statusTag(scope.row.status)">{{ statusMap[scope.row.status] || scope.row.status }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="subject" label="主题" min-width="240" />
        <el-table-column prop="to_addr" label="收件人" min-width="180" />
        <el-table-column prop="attempt_count" label="尝试" width="70" />
        <el-table-column prop="created_date" label="创建时间" width="160" />
        <el-table-column label="操作" width="200">
          <template slot-scope="scope">
            <el-button type="text" @click="showDetail(scope.row.id)">详情</el-button>
            <el-button
              v-if="['failed', 'exhausted', 'retry_wait'].includes(scope.row.status)"
              type="text"
              @click="doRetry(scope.row.id)"
            >重试</el-button>
            <el-button
              v-if="scope.row.status === 'success'"
              type="text"
              @click="doResend(scope.row.id)"
            >重发</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-pagination
        style="margin-top: 16px; text-align: right;"
        background
        layout="total, prev, pager, next"
        :total="total"
        :current-page="page"
        :page-size="pageSize"
        @current-change="onPageChange"
      />
    </el-card>

    <el-dialog title="发送详情" :visible.sync="detailVisible" width="900px">
      <div v-if="detail.record">
        <p><b>主题：</b>{{ detail.record.subject }}</p>
        <p><b>状态：</b>{{ statusMap[detail.record.status] || detail.record.status }}</p>
        <p><b>收件人：</b>{{ detail.record.to_addr }}</p>
        <p><b>附件：</b>
          <a v-if="detail.record.attachment_url" :href="detail.record.attachment_url" target="_blank">{{ detail.record.attachment_name }}</a>
          <span v-else>无</span>
        </p>
        <p><b>错误：</b>{{ detail.record.error_msg || '-' }}</p>
      </div>
      <el-table :data="detail.attempts || []" border size="mini">
        <el-table-column prop="attempt_no" label="第几次" width="80" />
        <el-table-column prop="trigger_source" label="来源" width="90" />
        <el-table-column prop="status" label="结果" width="110" />
        <el-table-column prop="error_msg" label="错误" min-width="200" />
        <el-table-column prop="started_at" label="开始" width="160">
          <template slot-scope="scope">{{ formatTs(scope.row.started_at) }}</template>
        </el-table-column>
        <el-table-column prop="finished_at" label="结束" width="160">
          <template slot-scope="scope">{{ formatTs(scope.row.finished_at) }}</template>
        </el-table-column>
      </el-table>
    </el-dialog>
  </div>
</template>

<script>
import {
  getEmailRecordList, getEmailRecordDetail, retryEmailRecord, resendEmailRecord
} from '@/api/email'

export default {
  name: 'EmailRecord',
  data () {
    return {
      loading: false,
      filter: { type: '', status: '', to: '' },
      dateRange: [],
      list: [],
      total: 0,
      page: 1,
      pageSize: 20,
      statusMap: {
        pending: '待发送',
        sending: '发送中',
        success: '成功',
        retry_wait: '待重试',
        failed: '失败',
        exhausted: '重试耗尽',
        canceled: '已取消'
      },
      detailVisible: false,
      detail: {}
    }
  },
  created () {
    this.search()
  },
  methods: {
    // 统一解析响应：兼容 {data:{...}} 包裹与直接返回
    unwrap (res) {
      const body = res && res.data ? res.data : res
      return body && body.data ? body.data : body
    },
    async search () {
      this.page = 1
      await this.loadList()
    },
    async loadList () {
      this.loading = true
      try {
        const params = Object.assign({ page: this.page, pageSize: this.pageSize }, this.filter)
        if (this.dateRange && this.dateRange.length === 2) {
          params.date_start = this.dateRange[0]
          params.date_end = this.dateRange[1]
        }
        const res = await getEmailRecordList(params)
        const data = this.unwrap(res) || {}
        this.list = data.list || []
        this.total = data.total_count || 0
      } finally {
        this.loading = false
      }
    },
    onPageChange (page) {
      this.page = page
      this.loadList()
    },
    async showDetail (id) {
      const res = await getEmailRecordDetail(id)
      this.detail = this.unwrap(res) || {}
      this.detailVisible = true
    },
    async doRetry (id) {
      await retryEmailRecord(id)
      this.$message.success('已提交重试')
      this.loadList()
    },
    async doResend (id) {
      try {
        await this.$confirm('确认重发该邮件？', '提示', { type: 'warning' })
      } catch (e) {
        return
      }
      await resendEmailRecord(id)
      this.$message.success('已提交重发')
      this.loadList()
    },
    statusTag (status) {
      if (status === 'success') return 'success'
      if (status === 'failed' || status === 'exhausted') return 'danger'
      if (status === 'retry_wait') return 'warning'
      return 'info'
    },
    formatTs (ts) {
      if (!ts) return '-'
      const d = new Date(ts * 1000)
      const pad = n => (n < 10 ? '0' + n : '' + n)
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    }
  }
}
</script>
